import APIV5 from '~/models/api';
import Link from '~/scripts/services/model/link';
import Relations from '~/scripts/services/relations';
import { DEFINED_BY } from '~/scripts/constants/common-kids';

/**
 * @typedef ClassDicoEntry
 * @property {import('@knitiv/api-client-javascript').KNode} class_kid
 * @property {string} id
 * @property {import('@knitiv/api-client-javascript').LinkerKNode} linker_kid
 * @property {string} name
 * @property {import('@knitiv/api-client-javascript').KNodeRelationId} relation_kid
 */

export default class QueryClassesController {
	/* @ngInject */
	/**
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {import('angular').IScope} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {import('~/scripts/services/model/node-service').NodeService} NodeService
	 * @param {*} Notification
	 */
	constructor(
		$injector,
		$scope,
		$timeout,
		$translate,
		NodeService,
		Notification,
	) {
		this.$injector = $injector;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$translate = $translate;
		this.$translate = $translate;
		this.NodeService = NodeService;
		this.Notification = Notification;

		/** @type {any[]} */
		this.children = [];

		/** @type {any[]} */
		this.classes = [];

		/** @type {ClassDicoEntry[]} */
		this.classDico = [];

		this.data = undefined;
		this.disabled = undefined;

		/** @type {?import('../query-editor/query-editor.controller').default} */
		this.editor = null;

		this.isLeaf = true;

		/** @type {?import('angular').INgModelController} */
		this.ngModelCtrl = null;

		/**
		 * @type {{displayTarget: boolean}}
		 */
		this.options = {
			displayTarget: false,
		};

		/** @type {string} */
		this.tabActive = 'definedby';

		/**
		 * @type {?{
		 *   linker_kid: import('@knitiv/api-client-javascript').LinkerKNode,
		 *   class_kid: import('@knitiv/api-client-javascript').KNode,
		 *   relation_kid: import('@knitiv/api-client-javascript').KNodeRelationId,
		 * }}
		 */
		this.targetModel = null;

		/** @type {string} */
		this.targetId = '';
	}

	/**
	 * @param {any} object
	 */
	async addFrom(object) {
		let isFromExist = false;

		for (const from of this.classes) {
			if (from !== undefined && from.node === object.kid) {
				isFromExist = true;
			}
		}

		if (isFromExist) {
			this.Notification.warning({
				title:   this.$translate.instant('error.title'),
				message: 'Cette classe a déjà été sélectionnée',
			});
		}
		else if (object && object.kid) {
			this.classes.length = 0;
			const data = await APIV5.instance.objectInfo({ kid: object.kid }, { id: 'query-classes.add-from' });
			this.classes.push({
				node:              object.kid,
				name:              object.name,
				type:              object.type || object.type_kid,
				inherits_extended: !data.error && data.parent_inherits.nb > 0 ? 0 : -1,
			});
			this.$timeout(() => {
				if (this.editor?.queryEditorForm) {
					this.editor.queryEditorForm.$setDirty();
				}
			});
		}
	}

	async initTreeTabs() {
		const result = await APIV5.instance.queryChildren(
			{ node_kid: this.classes[0].node },
			{ id: 'query-classes.init-tree-tabs' },
		);
		const path = [
			{
				kid: 'PATH_REL',
			},
		];
		return this.$timeout(() => {
			this.isLeaf = result.is_leaf;
			this.children = result.result.map(child => ({
				node:              child.kid,
				name:              child.name,
				linker_kid:        child.linker?.kid ?? '0',
				// @ts-ignore
				linker_info:       child.linker?.kid === '0' ? undefined : {
					name: child.linker?.name,
				},
				path:              [
					...path,
					{
						kid:  child.kid,
						flow: 'down',
						linker_kid: child.linker?.kid ?? '0',
						// @ts-ignore
						linker_info: child.linker?.kid === '0' ? undefined : {
							name: child.linker?.name,
						},
						name: child.name,
						type: child.type_kid,
					},
				],
				type:              child.type_kid,
				inherits_extended: 0,
			}));
		});
	}

	setTarget() {
		const dicoEntry = this.classDico.find(d => d.id === this.targetId);
		this.targetModel = dicoEntry ? {
			linker_kid:   dicoEntry.linker_kid,
			class_kid:    dicoEntry.class_kid,
			relation_kid: dicoEntry.relation_kid,
		} : null;
	}

	/**
	 * @param {import('angular').IOnChangesObject} changes
	 */
	$onChanges(changes) {
		if (changes.targetModel) {
			const link = new Link(this.targetModel);
			this.targetId = link.getId();
		}
	}

	$onInit() {
		this.$scope.$watch('$ctrl.classes', (newClasses, oldclasses) => {
			this.ngModelCtrl?.$validate();
			if (newClasses?.length > 0 && (oldclasses.length === 0 || newClasses[0].node !== oldclasses[0].node)) {
				(async () => this.initTreeTabs())();
			}
			return true;
		}, true);


		if (this.classes?.length > 0) {
			(async () => {
				await this.initTreeTabs();
				if (this.options.displayTarget) {
					const relations = await Relations.get(this.$injector);
					const data = await APIV5.instance.objectInfo({ kid: this.classes[0].node }, { id: 'query-classes.on-init' });

					if (data?.kndef.CLASS?.DICO) {
						this.classDico = Object
							.values(data.kndef.CLASS.DICO)
							.map(dicoItem => {
								const link = new Link(dicoItem);
								let name = link.getName();
								const relation = relations.find(r => r.kid === link.relation_kid);
								if (relation) {
									name = `${relation.getName()} ${name}`;
								}
								return {
									id:   link.getId(),
									name,
									linker_kid:   link.linker_kid,
									class_kid:    link.objnum2_kid ?? 'K_NODE;0',
									relation_kid: link.relation_kid ?? DEFINED_BY,
								};
							});
					}
				}
			})();
		}
		if (this.targetModel) {
			const link = new Link(this.targetModel);
			this.targetId = link.getId();
		}
	}
}
